import { useState, useEffect } from 'react'
import { publicCacheService } from '../utils/publicCacheService'

function AdminDataDebugger() {
  const [storageData, setStorageData] = useState<any>({})
  const [message, setMessage] = useState('')

  useEffect(() => {
    loadStorageData()
  }, [])

  const loadStorageData = () => {
    const data = {
      // Admin data
      classes: JSON.parse(localStorage.getItem('salty_mermaid_classes') || '[]'),
      kits: JSON.parse(localStorage.getItem('salty_mermaid_kits') || '[]'),
      gallery: JSON.parse(localStorage.getItem('salty_mermaid_gallery_items') || '[]'),
      images: JSON.parse(localStorage.getItem('salty_mermaid_images') || '[]'),
      
      // Server cache data (what public site reads)
      serverClasses: JSON.parse(localStorage.getItem('server_cache_classes') || '[]'),
      serverKits: JSON.parse(localStorage.getItem('server_cache_kits') || '[]'),
      serverGallery: JSON.parse(localStorage.getItem('server_cache_gallery') || '[]'),
      serverImages: JSON.parse(localStorage.getItem('server_cache_images') || '[]'),
    }
    setStorageData(data)
  }

  const migrateAdminToPublicCache = () => {
    try {
      // Migrate classes
      const classes = storageData.classes.map((cls: any) => ({
        id: cls.id,
        title: cls.title,
        description: cls.description,
        instructor: cls.instructor,
        date: cls.date,
        time: cls.time,
        duration: cls.duration,
        price: cls.price,
        capacity: cls.capacity,
        enrolled: cls.enrolled,
        level: cls.level,
        imageId: cls.imageId,
        isActive: cls.isActive,
        tags: cls.tags || [],
        materials: cls.materials || [],
        prerequisites: cls.prerequisites
      }))

      // Migrate kits
      const kits = storageData.kits.map((kit: any) => ({
        id: kit.id,
        name: kit.name,
        description: kit.description,
        price: kit.price,
        category: kit.category,
        inStock: kit.inStock,
        stockCount: kit.stockQuantity || 0,
        imageId: kit.imageId,
        isActive: kit.isActive,
        materials: kit.features || [],
        difficulty: kit.level
      }))

      // Migrate gallery items
      const gallery = storageData.gallery.map((item: any) => ({
        id: item.id,
        title: item.title,
        artist: item.artist,
        medium: item.medium,
        price: item.price,
        forSale: item.inStock,
        description: item.description,
        imageId: item.imageId,
        isActive: item.isActive,
        category: item.category,
        dateCreated: new Date().toISOString()
      }))

      // Migrate images
      const images = storageData.images.map((img: any) => ({
        id: img.id,
        filename: img.name,
        dataUrl: img.dataUrl,
        category: img.category,
        uploadDate: img.uploadedAt
      }))

      // Update public cache
      publicCacheService.updateClassesCache(classes)
      publicCacheService.updateKitsCache(kits)
      publicCacheService.updateGalleryCache(gallery)
      publicCacheService.updateImagesCache(images)

      setMessage(`Successfully migrated: ${classes.length} classes, ${kits.length} kits, ${gallery.length} gallery items, ${images.length} images`)
      
      // Reload data to show updated state
      setTimeout(loadStorageData, 500)
    } catch (error) {
      setMessage(`Migration failed: ${error}`)
    }
  }

  const clearAllData = () => {
    if (confirm('Clear ALL data? This cannot be undone!')) {
      const keys = [
        'salty_mermaid_classes', 'salty_mermaid_kits', 'salty_mermaid_gallery_items', 'salty_mermaid_images',
        'server_cache_classes', 'server_cache_kits', 'server_cache_gallery', 'server_cache_images'
      ]
      keys.forEach(key => localStorage.removeItem(key))
      setMessage('All data cleared')
      loadStorageData()
    }
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fs-3 fw-bold">Data Debugger</h2>
        <div className="d-flex gap-2">
          <button onClick={loadStorageData} className="btn btn-primary">Refresh</button>
          <button onClick={migrateAdminToPublicCache} className="btn btn-success">Migrate Admin → Server Cache</button>
          <button onClick={clearAllData} className="btn btn-danger">Clear All Data</button>
        </div>
      </div>

      {message && (
        <div className="alert alert-info mb-4">{message}</div>
      )}

      <div className="row g-4">
        {/* Admin Data */}
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h5>Admin Data (Source)</h5>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <strong>Classes:</strong> {storageData.classes?.length || 0}
                {storageData.classes?.length > 0 && (
                  <ul className="small mt-2">
                    {storageData.classes.slice(0, 3).map((cls: any) => (
                      <li key={cls.id}>{cls.title} (Active: {cls.isActive ? 'Yes' : 'No'})</li>
                    ))}
                    {storageData.classes.length > 3 && <li>... and {storageData.classes.length - 3} more</li>}
                  </ul>
                )}
              </div>
              
              <div className="mb-3">
                <strong>Kits:</strong> {storageData.kits?.length || 0}
                {storageData.kits?.length > 0 && (
                  <ul className="small mt-2">
                    {storageData.kits.slice(0, 3).map((kit: any) => (
                      <li key={kit.id}>{kit.name} (Active: {kit.isActive ? 'Yes' : 'No'})</li>
                    ))}
                    {storageData.kits.length > 3 && <li>... and {storageData.kits.length - 3} more</li>}
                  </ul>
                )}
              </div>
              
              <div className="mb-3">
                <strong>Gallery:</strong> {storageData.gallery?.length || 0}
                {storageData.gallery?.length > 0 && (
                  <ul className="small mt-2">
                    {storageData.gallery.slice(0, 3).map((item: any) => (
                      <li key={item.id}>{item.title} by {item.artist}</li>
                    ))}
                    {storageData.gallery.length > 3 && <li>... and {storageData.gallery.length - 3} more</li>}
                  </ul>
                )}
              </div>
              
              <div>
                <strong>Images:</strong> {storageData.images?.length || 0}
              </div>
            </div>
          </div>
        </div>

        {/* Server Cache */}
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h5>Server Cache (What Users See)</h5>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <strong>Server Classes:</strong> {storageData.serverClasses?.length || 0}
                {storageData.serverClasses?.length > 0 && (
                  <ul className="small mt-2">
                    {storageData.serverClasses.slice(0, 3).map((cls: any) => (
                      <li key={cls.id}>{cls.title} (Active: {cls.isActive ? 'Yes' : 'No'})</li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="mb-3">
                <strong>Server Kits:</strong> {storageData.serverKits?.length || 0}
                {storageData.serverKits?.length > 0 && (
                  <ul className="small mt-2">
                    {storageData.serverKits.slice(0, 3).map((kit: any) => (
                      <li key={kit.id}>{kit.name} (Active: {kit.isActive ? 'Yes' : 'No'})</li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="mb-3">
                <strong>Server Gallery:</strong> {storageData.serverGallery?.length || 0}
                {storageData.serverGallery?.length > 0 && (
                  <ul className="small mt-2">
                    {storageData.serverGallery.slice(0, 3).map((item: any) => (
                      <li key={item.id}>{item.title} by {item.artist}</li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <strong>Server Images:</strong> {storageData.serverImages?.length || 0}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Raw JSON (collapsible) */}
      <div className="mt-4">
        <details>
          <summary className="btn btn-outline-secondary">Show Raw JSON Data</summary>
          <pre className="bg-light p-3 mt-2 small" style={{maxHeight: '400px', overflow: 'auto'}}>
            {JSON.stringify(storageData, null, 2)}
          </pre>
        </details>
      </div>
    </div>
  )
}

export default AdminDataDebugger